import { z } from "zod";

// Password Login Schema (static version)
export const passwordLoginSchema = z.object({
  phoneNumber: z
    .string()
    .min(10, "Phone number must be 10 digits")
    .max(10, "Phone number must be 10 digits")
    .regex(/^\d+$/, "Phone number must contain only numbers"),
  password: z
    .string()
    .min(1, "Password is required")
    .max(50, "Password is too long"),
  acceptTerms: z.boolean().refine((value) => value === true, {
    message: "You must accept the terms and conditions",
  }),
});

// Conditional Password Login Schema Factory
export const createPasswordLoginSchema = (hasSelectedSchool: boolean) => {
  return z.object({
    phoneNumber: hasSelectedSchool
      ? z.string().min(1, "Phone number is required") // Minimal validation when school is selected
      : z
          .string()
          .min(10, "Phone number must be 10 digits")
          .max(10, "Phone number must be 10 digits")
          .regex(/^\d+$/, "Phone number must contain only numbers"),
    password: z
      .string()
      .min(1, "Password is required")
      .max(50, "Password is too long"),
    acceptTerms: z.boolean().refine((value) => value === true, {
      message: "You must accept the terms and conditions",
    }),
  });
};

// OTP Login Schema
export const otpLoginSchema = z.object({
  phoneNumber: z
    .string()
    .min(10, "Phone number must be 10 digits")
    .max(10, "Phone number must be 10 digits")
    .regex(/^\d+$/, "Phone number must contain only numbers"),
  otp: z
    .string()
    .min(4, "OTP must be 4 digits")
    .max(4, "OTP must be 4 digits")
    .regex(/^\d+$/, "OTP must contain only numbers"),
  acceptTerms: z.boolean().refine((value) => value === true, {
    message: "You must accept the terms and conditions",
  }),
});

// OTP Login with Phone Confirmation Schema (for the initial step)
export const otpLoginWithConfirmationSchema = z
  .object({
    phoneNumber: z
      .string()
      .min(10, "Phone number must be 10 digits")
      .max(10, "Phone number must be 10 digits")
      .regex(/^\d+$/, "Phone number must contain only numbers"),
    confirmPhoneNumber: z
      .string()
      .min(10, "Phone number must be 10 digits")
      .max(10, "Phone number must be 10 digits")
      .regex(/^\d+$/, "Phone number must contain only numbers"),
    acceptTerms: z.boolean().refine((value) => value === true, {
      message: "You must accept the terms and conditions",
    }),
  })
  .refine((data) => data.phoneNumber === data.confirmPhoneNumber, {
    message: "Phone numbers must match",
    path: ["confirmPhoneNumber"],
  });

// Conditional OTP Login with Phone Confirmation Schema Factory
export const createOTPLoginWithConfirmationSchema = (
  hasSelectedSchool: boolean
) => {
  const baseSchema = z.object({
    phoneNumber: hasSelectedSchool
      ? z.string().min(1, "Phone number is required") // Minimal validation when school is selected
      : z
          .string()
          .min(10, "Phone number must be 10 digits")
          .max(10, "Phone number must be 10 digits")
          .regex(/^\d+$/, "Phone number must contain only numbers"),
    confirmPhoneNumber: z
      .string()
      .min(10, "Phone number must be 10 digits")
      .max(10, "Phone number must be 10 digits")
      .regex(/^\d+$/, "Phone number must contain only numbers"),
    acceptTerms: z.boolean().refine((value) => value === true, {
      message: "You must accept the terms and conditions",
    }),
  });

  // If no selected school, add phone number matching validation
  if (!hasSelectedSchool) {
    return baseSchema.refine(
      (data) => data.phoneNumber === data.confirmPhoneNumber,
      {
        message: "Phone numbers must match",
        path: ["confirmPhoneNumber"],
      }
    );
  }

  // If selected school, skip phone number matching
  return baseSchema;
};

// Export types
export type PasswordLoginData = z.infer<typeof passwordLoginSchema>;
export type OTPLoginData = z.infer<typeof otpLoginSchema>;
export type OTPLoginWithConfirmationData = z.infer<
  typeof otpLoginWithConfirmationSchema
>;

// Validation helper functions
export const validatePasswordLogin = (
  data: unknown,
  hasSelectedSchool?: boolean
) => {
  const schema =
    hasSelectedSchool !== undefined
      ? createPasswordLoginSchema(hasSelectedSchool)
      : passwordLoginSchema;
  return schema.safeParse(data);
};

export const validateOTPLogin = (data: unknown) => {
  return otpLoginSchema.safeParse(data);
};

export const validateOTPLoginWithConfirmation = (
  data: unknown,
  hasSelectedSchool?: boolean
) => {
  const schema =
    hasSelectedSchool !== undefined
      ? createOTPLoginWithConfirmationSchema(hasSelectedSchool)
      : otpLoginWithConfirmationSchema;
  return schema.safeParse(data);
};
